/**
 * Générateur, Attendu 187, O5 « Il transforme une figure par translation. »
 * Niveau : 4ème | Thème #11 Géométrie plane (O5) | Espace et géométrie.
 * Source prof : « TANOED EXERCISEUR ESPACE ET GEOMETRIE 4EME », O5 : le doc est
 * muet en exercices, banque 4e.
 *
 * Historique (juillet 2026, état remplacé par la revue du 2026-09-23) : la
 * translation y était définie par un vecteur donné en coordonnées (vx ; vy),
 * en cinq cartes (image d'un point, construire l'image d'une figure, lire le
 * vecteur, composer deux translations, reconnaître la translation parmi quatre
 * vecteurs) ; gen_182 gardait la définition « A → B » et les propriétés.
 *
 * Revue 2026-09-23 (vague 4, la translation ; mode auto, décisions du
 * coordinateur, cadrage project_cadrage_182-187-188) :
 * Plafond : « Il transforme une figure par translation. » (attendus de fin de
 * 4e, Eduscol, géométrie plane) ; exemple officiel : une figure construite par
 * translations (chevrons). Cours du professeur : « Une translation fait glisser
 * une figure selon une flèche », « M' est l'image de M par la translation qui
 * envoie A en B », « on déplace la flèche », « en comptant les carreaux ». Le
 * mot « vecteur » n'est pas dans le cours et aucune ligne du plafond ne parle
 * de vecteur ni de coordonnées d'un vecteur (objectifs de 3e) : toutes les
 * cartes parlent de flèche, de carreaux et de « la translation qui transforme
 * A en B », jamais de vecteur ni de déplacement écrit en coordonnées.
 *   - « Construire l'image d'une figure » (construire_placer_points_2d, clé
 *     gardée) : reprend la carte de 182 « Construire l'image par une
 *     translation » (A et B posés, « la translation qui transforme A en B »,
 *     P, Q, R posés, l'élève place P', Q', R'), déplacée ici parce que c'est la
 *     ligne « Il transforme une figure par translation ». Énoncé qui dit enfin
 *     que P, Q, R sont les sommets d'un triangle (le composant ne dessine que
 *     des points). Déplacements dans toutes les directions (182 allait
 *     toujours vers la droite). Mode auto, À ARBITRER : le composant repère ne
 *     dessine ni flèche ni côté, on garde A et B posés SANS flèche (l'élève
 *     compte de A à B) ; autres voies : une figure statique au-dessus du
 *     repère (deux quadrillages à l'écran), ou un décor dans le composant
 *     (zone protégée, accord explicite).
 *   - « Image d'un point par translation » (image_point_texte_trou_saisie,
 *     clé gardée) : reprend la carte de 182 « Coordonnées de l'image » (A, B,
 *     P et la flèche de A vers B sur un repère gradué). P différent de A et de
 *     B, à au moins un carreau de la flèche (avant : P sur B dans 0,8 % des
 *     tirages, étiquettes superposées ; P sur la flèche dans 1,7 %), image
 *     jamais posée sur A. Mode auto, À ARBITRER : les coordonnées de A, B et P
 *     ne sont plus dans le texte, l'élève les lit sur le repère gradué et
 *     compte les carreaux comme dans le cours. Parenthèse sur le signe moins
 *     supprimée (aucune réponse négative, l'image reste dans le repère).
 *   - « Compter les carreaux d'une translation » (carreaux_texte_trou_saisie,
 *     ancienne « Lire le vecteur d'une translation », clé
 *     lire_vecteur_texte_trou_saisie RENOMMÉE) : « Cette translation fait
 *     glisser une figure de ⬚ carreaux vers la droite et de ⬚ carreaux vers
 *     le bas. » (« faire glisser » demande un complément ; « une figure » est
 *     le mot du cours, même tournure que 188) ; mots de
 *     direction fixés par le tirage, seuls les nombres sont des trous ;
 *     quadrillage sans axes, la flèche seule. Écart à la décision (mesure en
 *     tête du rapport) : les trous vont de 2 à 6 et de 2 à 4, pas de 1 à 6 et
 *     de 1 à 4, parce qu'avec l'accord « 1 carreau » le singulier affiché
 *     devant le trou donne la réponse 1 (37,5 % des tirages auraient eu un
 *     trou trahi).
 *   - « Composer deux translations » (composer_texte_trou_saisie) : SORTIE.
 *     Enchaîner deux translations (la somme) est un objectif de 3e (BO 2025
 *     cycle 4, l. 1122), et les coordonnées d'un vecteur ne sont nulle part au
 *     collège.
 *   - « La flèche d'une translation » (reconnaitre_qcm_unique, clé gardée,
 *     ancienne « Reconnaître la translation ») : QCM de quatre flèches
 *     numérotées de 1 à 4 tracées sur un quadrillage, A et B posés ailleurs,
 *     sans flèche entre eux : « Quelle flèche schématise la translation qui
 *     transforme A en B ? ». Grille croisée sens × carreaux : clé (a ; b),
 *     sens_inverse (−a ; −b), carreaux_inverses (b ; a), les_deux (−b ; −a),
 *     a et b non nuls et |a| ≠ |b| (la décision disait a ≠ b ; il faut aussi
 *     a ≠ −b, sinon sens_inverse et carreaux_inverses sont la même flèche).
 *     Quatre flèches de même longueur, deux paires de flèches opposées : ni
 *     « seule paire ± » (avant : 90,6 % des tirages se réduisaient à deux
 *     options sans calcul), ni flèche « du milieu ». C'est le QCM de
 *     l'attendu (dépouillable en classe par QR code).
 * Figures : SVG en imageUrl avec un alt qui décrit sans donner la réponse
 * (avant : imageData, alt « Figure », la question ne se réenregistrait pas),
 * trait arrêté avant le point d'arrivée, pointe de 10 px (avant : 7 px, cachée
 * sous le point B), noms de points placés hors des traits (svgQuadrillage,
 * ajouté à transformations.js). Déplacement sacrifié du cours (4 carreaux à
 * droite, 1 vers le haut) exclu de toutes les cartes, aucune flèche du QCM ne
 * le dessine. Pas de rappel brouillon (tout se fait sur la figure). Aucun
 * solveur test-side ne couvre 187 ; les deux cartes reprises de 182 sont
 * relues par le solveur de 1245 qui les délègue
 * (tests/solvers/transformations-3e.js).
 *
 * ── Révision 2026-09-24 (demande du prof, validée) ────────────────────────
 *   - « Construire l'image d'une figure » : l'arbitrage du 23/09 (« le
 *     composant ne dessine ni flèche ni côté ») est levé : le format
 *     placer_points_2d a gagné trois champs (FORMATS §9bis,
 *     utils/repereDecor.js). QUADRILLAGE sans axes à carreaux carrés, 14 × 10
 *     (au lieu du repère 12 × 12 à carreaux déformés) ; le triangle PQR et la
 *     flèche de A vers B DESSINÉS ; les sommets P', Q', R' RELIÉS au fur et à
 *     mesure, le triangle image se ferme au dernier ; « La flèche schématise
 *     la translation qui transforme A en B. Construis l'image du triangle PQR
 *     par cette translation : place P', Q' et R'. » ; la flèche ne traverse
 *     ni le triangle ni son image ; triangle bien formé (côtés d'au moins 2
 *     carreaux, angles d'au moins 25°). Tout reste dans le quadrillage (déjà
 *     garanti, et vérifié au harnais : A-PP-BORNES sur les points de
 *     l'énoncé). Carte reprise par gen_1245 (3e) : elle hérite du tout.
 *
 * ── Révision 2026-09-25 (décision prof) ──────────────────────────────────
 *   - « L'image d'un sommet » (sommet_qcm_unique) ARRIVE de 189 : la figure
 *     verte et son image orange dessinées (figTranslation, la figure de 189),
 *     le nom de l'image lu à partir d'un autre sommet, dans un sens ou dans
 *     l'autre (RAMI → BELO) ; « Quelle est l'image du point B ? », les quatre
 *     lettres de l'image en choix. Elle ne déterminait aucune grandeur (donc
 *     pas 189) ; elle fait LIRE la correspondance d'une figure et de son
 *     image (ex. 3 a du prof), ce qu'aucune carte d'ici ne demandait :
 *     partout ailleurs l'image est notée avec des primes ou par la flèche
 *     de A vers B. Second QCM de l'attendu, assumé (décision prof). Ni
 *     calculatrice ni rappel brouillon. Le tirage commun (tirerImage,
 *     phraseImage, figureImage) est dans figTranslation.js.
 */

import {
    randInt, randomChoice, shuffle, tex, makeQcm, makeTexteTrou, basePayload,
} from '../../../utils';
import { BLEU, ORANGE, VERT } from './geometrie';
import { svgQuadrillage, imageEnonce, distPointSegment } from '../transformations/transformations';
import { tirerImage, phraseImage, figureImage } from './figTranslation';

const ATTENDU_ID = 187;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CONS = 'Construire l\'image d\'une figure';
const L_IMG = 'Image d\'un point par translation';
const L_CARREAUX = 'Compter les carreaux d\'une translation';
const L_RECO = 'La flèche d\'une translation';
const L_SOMMET = 'L\'image d\'un sommet';

const GMAX = 12;
const egal = (p, q) => p[0] === q[0] && p[1] === q[1];
const plus = (p, v) => [p[0] + v[0], p[1] + v[1]];
const moins = (p, q) => [p[0] - q[0], p[1] - q[1]];
const dans = (p, lo, hi) => p[0] >= lo && p[0] <= hi && p[1] >= lo && p[1] <= hi;
const distincts = (pts) => new Set(pts.map((p) => p.join(','))).size === pts.length;
const aire2 = ([a, b, c]) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

// Le déplacement de l'exemple du cours (p. 3 : 4 carreaux à droite, 1 vers le
// haut) est sacrifié : réservé à l'explication, il ne sort d'aucune carte.
const SACRIFIE = [4, 1];

/** Déplacement dans toutes les directions : |horizontal| ≤ 6 et |vertical| ≤ 4 carreaux (plages du cadrage), au moins 2 carreaux sur un axe, jamais le sacrifié. */
const tirerDeplacement = () => {
    for (let e = 0; e < 60; e++) {
        const v = [randInt(-6, 6), randInt(-4, 4)];
        if (Math.max(Math.abs(v[0]), Math.abs(v[1])) >= 2 && !egal(v, SACRIFIE)) return v;
    }
    return [3, -2];
};

// ═════════ Carte « Construire l'image d'une figure » (placer_points_2d) ═════════

// Quadrillage en largeur (carreaux carrés, 2026-09-24) : 14 × 10 carreaux.
const XMAX_CONS = 14;
const YMAX_CONS = 10;
const dansCons = (p) => p[0] >= 0 && p[0] <= XMAX_CONS && p[1] >= 0 && p[1] <= YMAX_CONS;

// La flèche ne traverse ni le triangle ni son image : sinon le dessin est illisible.
const orient = (p, q, r) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
const surSegment = (p, a, b) => orient(a, b, p) === 0
    && Math.min(a[0], b[0]) <= p[0] && p[0] <= Math.max(a[0], b[0])
    && Math.min(a[1], b[1]) <= p[1] && p[1] <= Math.max(a[1], b[1]);
const seCoupent = (a, b, c, d) => (orient(a, b, c) !== orient(a, b, d) && orient(c, d, a) !== orient(c, d, b))
    || surSegment(c, a, b) || surSegment(d, a, b) || surSegment(a, c, d) || surSegment(b, c, d);
const dansTriangle = (p, [a, b, c]) => {
    const d = [orient(a, b, p), orient(b, c, p), orient(c, a, p)];
    return d.every((x) => x >= 0) || d.every((x) => x <= 0);
};
const flecheLibre = (A, B, T) => [0, 1, 2].every((i) => !seCoupent(A, B, T[i], T[(i + 1) % 3]))
    && !dansTriangle(A, T) && !dansTriangle(B, T);

// Un vrai triangle à l'œil : côtés d'au moins 2 carreaux, angles d'au moins 25°
// (vu en PNG : une aire minimale laissait passer des lames de quelques degrés).
const angle = (p, q, r) => {
    const u = [q[0] - p[0], q[1] - p[1]]; const w = [r[0] - p[0], r[1] - p[1]];
    return (Math.acos((u[0] * w[0] + u[1] * w[1]) / (Math.hypot(u[0], u[1]) * Math.hypot(w[0], w[1]))) * 180) / Math.PI;
};
const bienForme = ([a, b, c]) => Math.min(Math.hypot(b[0] - a[0], b[1] - a[1]), Math.hypot(c[0] - b[0], c[1] - b[1]), Math.hypot(a[0] - c[0], a[1] - c[1])) >= 2
    && Math.min(angle(a, b, c), angle(b, c, a), angle(c, a, b)) >= 25;

const construireValide = (A, B, S, v) => {
    const img = S.map((p) => plus(p, v));
    return !egal(v, SACRIFIE) && Math.max(Math.abs(v[0]), Math.abs(v[1])) >= 2
        && egal(moins(B, A), v) && dansCons(A) && dansCons(B)
        && S.every(dansCons) && img.every(dansCons)
        && distincts([A, B, ...S, ...img]) && Math.abs(aire2(S)) >= 4 && bienForme(S)
        && flecheLibre(A, B, S) && flecheLibre(A, B, img);
};

const payloadConstruire = (A, B, S, v) => {
    if (!construireValide(A, B, S, v)) throw new Error('gen_187 construire : tirage invalide');
    const noms = ['P', 'Q', 'R'];
    const dejaPlaces = [
        { x: A[0], y: A[1], label: 'A' },
        { x: B[0], y: B[1], label: 'B' },
        ...S.map((p, i) => ({ x: p[0], y: p[1], label: noms[i] })),
    ];
    const bonne = {};
    S.forEach((p, i) => { const q = plus(p, v); bonne[`${noms[i]}'`] = `${q[0]};${q[1]}`; });
    // Second chemin : relire A, B et les sommets dans dejaPlaces (ce que voit l'élève).
    const lu = Object.fromEntries(dejaPlaces.map((d) => [d.label, [d.x, d.y]]));
    const w = moins(lu.B, lu.A);
    noms.forEach((n) => { const q = plus(lu[n], w); if (bonne[`${n}'`] !== `${q[0]};${q[1]}`) throw new Error('gen_187 construire : cible'); });
    const payload = basePayload('association',
        '<p>La flèche schématise la translation qui transforme A en B.</p>'
        + '<p><b>Construis l\'image du triangle PQR par cette translation : place P\', Q\' et R\'.</b></p>',
        {
            subType: 'placer_points_2d',
            // quadrillage sans axes, le triangle et la flèche dessinés, l'image reliée au fur et à mesure (prof 2026-09-24)
            repere: { xMin: 0, yMin: 0, xMax: XMAX_CONS, yMax: YMAX_CONS, xStep: 1, yStep: 1, quadrillage: true },
            pointsToPlace: noms.map((n) => `${n}'`),
            dejaPlaces,
            decor: { polygones: [noms], fleches: [['A', 'B']] },
            relier: noms.map((n) => `${n}'`),
            bonne_reponse: JSON.stringify(bonne),
        },
        opts(L_CONS));
    payload._meta.params = { A, B, v, sommets: S };
    return payload;
};

const genConstruire = () => {
    for (let essai = 0; essai < 400; essai++) {
        const v = tirerDeplacement();
        const A = [randInt(0, XMAX_CONS), randInt(0, YMAX_CONS)];
        const B = plus(A, v);
        if (!dansCons(B)) continue;
        const S = [];
        for (let k = 0; k < 40 && S.length < 3; k++) {
            const p = [randInt(0, XMAX_CONS), randInt(0, YMAX_CONS)];
            if (dansCons(plus(p, v)) && !S.some((q) => egal(q, p))) S.push(p);
        }
        if (S.length === 3 && construireValide(A, B, S, v)) return payloadConstruire(A, B, S, v);
    }
    // Repli déterministe, vérifié par construireValide dans payloadConstruire.
    return payloadConstruire([1, 1], [4, 3], [[1, 5], [4, 8], [6, 4]], [3, 2]);
};

// ═════════ Carte « Image d'un point par translation » (texte à trous, saisie) ═════════

const imagePointValide = (A, B, P, v) => {
    const img = plus(P, v);
    return !egal(v, SACRIFIE) && Math.max(Math.abs(v[0]), Math.abs(v[1])) >= 2 && egal(moins(B, A), v)
        && dans(A, 1, 11) && dans(B, 1, 11) && dans(P, 1, 11) && dans(img, 0, GMAX)
        && !egal(P, A) && !egal(P, B) && !egal(img, A)
        && distPointSegment(P, A, B) >= 1;
};

const payloadImagePoint = (A, B, P, v) => {
    if (!imagePointValide(A, B, P, v)) throw new Error('gen_187 image_point : tirage invalide');
    const img = plus(P, v);
    // Second chemin : ABP'P est un parallélogramme, P' = B + (P − A).
    if (!egal(img, plus(B, moins(P, A)))) throw new Error('gen_187 image_point : image');
    const svg = svgQuadrillage({
        xMin: 0, xMax: GMAX, yMin: 0, yMax: GMAX, axes: true,
        fleches: [{ a: A, b: B, couleur: VERT }],
        points: [
            { x: A[0], y: A[1], nom: 'A', couleur: VERT, pref: [-v[0], -v[1]] },
            { x: B[0], y: B[1], nom: 'B', couleur: VERT, pref: v },
            { x: P[0], y: P[1], nom: 'P', couleur: BLEU },
        ],
    });
    const payload = makeTexteTrou(
        `L'image P' a pour abscisse {{${img[0]}}} et pour ordonnée {{${img[1]}}}.`,
        '<p>Dans le repère, la flèche schématise la translation qui transforme A en B.</p>'
        + '<p><b>Quelles sont les coordonnées de l\'image P\' du point P par cette translation ?</b></p>',
        {
            ...opts(L_IMG), mode: 'saisie',
            elements_additionnels: imageEnonce(svg,
                'Repère gradué de 0 à 12 sur chaque axe : les points A, B et P, et la flèche verte qui va de A à B.'),
        });
    payload._meta.params = { A, B, P, v };
    return payload;
};

const genImagePoint = () => {
    for (let essai = 0; essai < 400; essai++) {
        const v = tirerDeplacement();
        const A = [randInt(1, 11), randInt(1, 11)];
        const P = [randInt(1, 11), randInt(1, 11)];
        if (imagePointValide(A, plus(A, v), P, v)) return payloadImagePoint(A, plus(A, v), P, v);
    }
    return payloadImagePoint([2, 3], [5, 5], [3, 8], [3, 2]);
};

// ═════════ Carte « Compter les carreaux d'une translation » (texte à trous, saisie) ═════════

const LX = 10;
const LY = 7;

const genCarreaux = () => {
    const sx = randomChoice([1, -1]);
    const sy = randomChoice([1, -1]);
    // 2 à 6 et 2 à 4, pas 1 : « 1 carreau » au singulier devant le trou donnerait la réponse.
    const nx = randInt(2, 6);
    const ny = randInt(2, 4);
    const v = [sx * nx, sy * ny];
    const T = [
        sx > 0 ? randInt(1, LX - 1 - nx) : randInt(1 + nx, LX - 1),
        sy > 0 ? randInt(1, LY - 1 - ny) : randInt(1 + ny, LY - 1),
    ];
    const svg = svgQuadrillage({ xMin: 0, xMax: LX, yMin: 0, yMax: LY, fleches: [{ a: T, b: plus(T, v), couleur: VERT }] });
    // Second chemin : la flèche relue dans la figure donne les deux trous et les deux mots.
    const m = svg.match(/data-de="(-?\d+),(-?\d+)" data-a="(-?\d+),(-?\d+)"/);
    const lu = [+m[3] - +m[1], +m[4] - +m[2]];
    if (Math.abs(lu[0]) !== nx || Math.abs(lu[1]) !== ny || Math.sign(lu[0]) !== sx || Math.sign(lu[1]) !== sy) {
        throw new Error('gen_187 carreaux : flèche');
    }
    if (![T, plus(T, v)].every((p) => p[0] >= 1 && p[0] <= LX - 1 && p[1] >= 1 && p[1] <= LY - 1)) throw new Error('gen_187 carreaux : cadre');
    const payload = makeTexteTrou(
        `Cette translation fait glisser une figure de {{${nx}}} carreaux vers la ${sx > 0 ? 'droite' : 'gauche'} `
        + `et de {{${ny}}} carreaux vers le ${sy > 0 ? 'haut' : 'bas'}.`,
        '<p>Sur le quadrillage, la flèche schématise une translation.</p><p><b>Complète la phrase.</b></p>',
        { ...opts(L_CARREAUX), mode: 'saisie', elements_additionnels: imageEnonce(svg, 'Quadrillage : une flèche verte.') });
    payload._meta.params = { depart: T, v };
    return payload;
};

// ═════════ Carte « L'image d'un sommet » (QCM, la figure nommée et son image) ═════════

/**
 * Venue de 189 le 2026-09-25 : l'élève doit lire sur la figure quel sommet
 * est l'image de quel sommet, puisque le nom de l'image ne suit pas l'ordre
 * du nom de départ. Piège : la lettre qui a la même place dans le nom de
 * l'image (correspondance_par_les_noms), jamais la clé par construction ;
 * les deux autres lettres de l'image complètent les quatre choix.
 */
const genSommet = () => {
    for (let essai = 0; essai < 100; essai++) {
        const t = tirerImage();
        const i = randInt(0, 3);                                               // le sommet de départ demandé
        const bonne = t.nomsImage[i];
        const piege = t.nomImage[i];                                           // la lettre qui a la même place dans le nom de l'image
        if (piege === bonne) continue;
        const autres = t.nomsImage.filter((l) => l !== bonne && l !== piege);
        const texte = `${phraseImage(t)}<p><b>Quelle est l'image du point ${tex(t.noms[i])} par cette translation ?</b></p>`;
        const payload = makeQcm([bonne, piege, ...autres].map(tex), tex(bonne), texte, { ...opts(L_SOMMET), elements_additionnels: figureImage(t, [], []) });
        payload._meta.params = {
            nom: t.nom, nomImage: t.nomImage, sommet: t.noms[i],
            distracteurs: [{ code: 'correspondance_par_les_noms' }, { code: 'autre_sommet' }, { code: 'autre_sommet' }],
        };
        return payload;
    }
    throw new Error('gen_187 : image d\'un sommet sans question');
};

// ═════════ Carte « La flèche d'une translation » (QCM, quatre flèches numérotées) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'jamais « vecteur », jamais de déplacement écrit en coordonnées : la translation est « la translation qui transforme A en B », schématisée par une flèche (cours du professeur)',
        'le déplacement sacrifié du cours (4 carreaux à droite, 1 vers le haut) ne sort jamais, ni comme clé ni comme flèche distractrice',
    ],
    reconnaitre_qcm_unique: {
        support: 'A et B posés sur un quadrillage de 18 × 12 carreaux, sans flèche entre eux ; quatre flèches numérotées de 1 à 4, chacune dans sa case de 6 × 6 carreaux, loin de A et B ; options « Flèche ① » à « Flèche ④ »',
        cle: 'la flèche (a ; b) qui va comme de A à B : même direction, même sens, même longueur',
        familles: {
            sens_inverse: '(−a ; −b) : la bonne direction et la bonne longueur, dans l\'autre sens (la translation qui transforme B en A), l\'erreur principale du cadrage',
            carreaux_inverses: '(b ; a) : les carreaux horizontaux et verticaux échangés',
            les_deux: '(−b ; −a) : les deux erreurs à la fois, pour que la grille soit croisée',
        },
        supprimees: {
            longueur_fausse: 'écartée : une flèche plus longue d\'un carreau se reconnaîtrait à sa seule longueur, alors que les quatre flèches de la grille croisée ont toutes la même',
        },
        garantie: 'a et b non nuls, |a| et |b| de 1 à 4 et différents : les quatre flèches sont deux à deux distinctes et de même longueur, elles forment deux paires de flèches opposées, donc ni la longueur, ni une paire ± unique, ni une flèche « du milieu » ne désignent la clé ; la case de chaque flèche et la case de A et B sont tirées au hasard, les numéros suivent l\'ordre de lecture des cases',
    },
    sommet_qcm_unique: {
        support: 'la figure verte et son image orange (figTranslation), le nom de l\'image lu à partir d\'un autre sommet, dans un sens ou dans l\'autre ; options : les quatre lettres de l\'image (venue de 189 le 2026-09-25)',
        cle: 'la lettre de l\'image du sommet demandé, lue sur la figure',
        familles: {
            correspondance_par_les_noms: 'la lettre qui a la même place dans le nom de l\'image que le sommet dans le nom de départ : jamais la clé, par construction (les tirages où elle coïnciderait sont rejoués)',
            autre_sommet: 'les deux autres lettres de l\'image',
        },
    },
};

const CERCLES = ['①', '②', '③', '④'];

/** Case c (0 à 5, ordre de lecture, trois colonnes et deux rangées de 6 × 6 carreaux) : un départ tel que la flèche w tienne à au moins un carreau du bord de la case. */
const departDansCase = (c, w) => {
    const x1 = 6 * (c % 3) + 1; const x5 = x1 + 4;
    const y1 = c < 3 ? 7 : 1; const y5 = y1 + 4;
    return [
        w[0] > 0 ? randInt(x1, x5 - w[0]) : randInt(x1 - w[0], x5),
        w[1] > 0 ? randInt(y1, y5 - w[1]) : randInt(y1 - w[1], y5),
    ];
};

const genReconnaitre = () => {
    let a = 3;
    let b = -1;
    for (let e = 0; e < 60; e++) {
        const ca = randInt(1, 4) * randomChoice([1, -1]);
        const cb = randInt(1, 4) * randomChoice([1, -1]);
        if (Math.abs(ca) === Math.abs(cb)) continue;
        // famille du sacrifié : aucune des quatre flèches ne doit être (4 ; 1)
        if ([[ca, cb], [-ca, -cb], [cb, ca], [-cb, -ca]].some((w) => egal(w, SACRIFIE))) continue;
        a = ca; b = cb;
        break;
    }
    const familles = [
        { w: [a, b], code: 'cle' },
        { w: [-a, -b], code: 'sens_inverse' },
        { w: [b, a], code: 'carreaux_inverses' },
        { w: [-b, -a], code: 'les_deux' },
    ];
    const cases = shuffle([0, 1, 2, 3, 4, 5]);
    const A = departDansCase(cases[0], [a, b]);
    const B = plus(A, [a, b]);
    const fleches = shuffle(familles).map((f, k) => ({ ...f, case: cases[k + 1], depart: departDansCase(cases[k + 1], f.w) }))
        .sort((f1, f2) => f1.case - f2.case)
        .map((f, k) => ({ ...f, numero: k + 1 }));
    const svg = svgQuadrillage({
        xMin: 0, xMax: 18, yMin: 0, yMax: 12,
        fleches: fleches.map((f) => ({ a: f.depart, b: plus(f.depart, f.w), couleur: VERT, numero: f.numero })),
        points: [
            { x: A[0], y: A[1], nom: 'A', couleur: BLEU, pref: [-a, -b] },
            { x: B[0], y: B[1], nom: 'B', couleur: ORANGE, pref: [a, b] },
        ],
    });
    // Second chemin : relire la FIGURE rendue (points A et B, flèches numérotées) ; une seule flèche va
    // comme de A à B, les quatre ont la même longueur, sont distinctes, aucune n'est le sacrifié.
    const xy = (s) => s.split(',').map(Number);
    const lus = Object.fromEntries([...svg.matchAll(/<g class="pt" data-nom="([AB])" data-xy="(-?\d+,-?\d+)"/g)].map((m) => [m[1], xy(m[2])]));
    const flLues = [...svg.matchAll(/<g class="fl" data-de="(-?\d+,-?\d+)" data-a="(-?\d+,-?\d+)" data-numero="(\d)"/g)]
        .map((m) => ({ numero: +m[3], w: moins(xy(m[2]), xy(m[1])) }));
    const ab = moins(lus.B, lus.A);
    const bonnes = flLues.filter((f) => egal(f.w, ab));
    if (flLues.length !== 4 || bonnes.length !== 1 || new Set(flLues.map((f) => f.w[0] ** 2 + f.w[1] ** 2)).size !== 1
        || !distincts(flLues.map((f) => f.w)) || flLues.some((f) => egal(f.w, SACRIFIE))
        || bonnes[0].numero !== fleches.find((f) => f.code === 'cle').numero) throw new Error('gen_187 reconnaitre : grille');
    const options = CERCLES.map((c) => `Flèche ${c}`);
    const payload = makeQcm(options, options[bonnes[0].numero - 1],
        '<p>Sur le quadrillage, on a placé les points A et B et quatre flèches numérotées.</p>'
        + '<p><b>Quelle flèche schématise la translation qui transforme A en B ?</b></p>',
        { ...opts(L_RECO), elements_additionnels: imageEnonce(svg, 'Quadrillage : les points A et B, et quatre flèches vertes numérotées de 1 à 4.') });
    payload._meta.params = {
        A, B, cle: [a, b], numeroCle: bonnes[0].numero,
        distracteurs: fleches.filter((f) => f.code !== 'cle').map((f) => ({ code: f.code, numero: f.numero })),
    };
    return payload;
};

// ═════════ Registre ═════════

const GENERATORS = {
    carreaux_texte_trou_saisie: genCarreaux,
    reconnaitre_qcm_unique: genReconnaitre,
    image_point_texte_trou_saisie: genImagePoint,
    sommet_qcm_unique: genSommet,
    construire_placer_points_2d: genConstruire,
};

const CAT = [
    { id: 'carreaux', label: L_CARREAUX, key: 'carreaux_texte_trou_saisie', fmt: 'Saisie' },
    { id: 'reconnaitre', label: L_RECO, key: 'reconnaitre_qcm_unique', fmt: 'QCM' },
    { id: 'image_point', label: L_IMG, key: 'image_point_texte_trou_saisie', fmt: 'Saisie' },
    { id: 'sommet', label: L_SOMMET, key: 'sommet_qcm_unique', fmt: 'QCM' },
    { id: 'construire', label: L_CONS, key: 'construire_placer_points_2d', fmt: 'Placer (interactif)' },
];

export const CATEGORIES = CAT.map((c) => ({
    id: c.id, label: c.label, formatsList: [{ key: c.key, label: c.fmt }],
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { GENERATORS, tirerDeplacement, SACRIFIE };
